import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, Table, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { applicationService, tripService } from '../../services/api';
import { format } from 'date-fns';
import Pagination from '../Pagination';

const ApplicationManagement = () => {
  const [applications, setApplications] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [adminComments, setAdminComments] = useState('');
  const [actionSuccess, setActionSuccess] = useState(null);
  const [filterTrip, setFilterTrip] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch all applications and trips for filter
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch applications
        const applicationResponse = await applicationService.getAllApplications();
        setApplications(applicationResponse.data);
        
        // Fetch trips for filter dropdown
        const tripsResponse = await tripService.getAllTrips();
        setTrips(tripsResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load applications. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Open application details modal
  const handleOpenDetailsModal = (application) => {
    setSelectedApplication(application);
    setAdminComments(application.adminComments || '');
    setShowDetailsModal(true);
  };

  // Update application status
  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      await applicationService.updateApplicationStatus(applicationId, {
        status: newStatus,
        adminComments: adminComments
      });
      
      // Update local state
      setApplications(applications.map(app => 
        app._id === applicationId 
          ? { ...app, status: newStatus, adminComments: adminComments } 
          : app
      ));
      
      // Close modal
      setShowDetailsModal(false);
      
      // Show success message
      setActionSuccess(`Application ${newStatus} successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating application:', err);
      setError(err.response?.data?.message || 'Failed to update application. Please try again.');
    }
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    // Filter by trip
    if (filterTrip && app.tripId._id !== filterTrip) {
      return false;
    }
    
    // Filter by status
    if (filterStatus && app.status !== filterStatus) {
      return false;
    }
    
    return true;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterTrip, filterStatus]);

  // Paginated applications
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredApplications.slice(startIndex, endIndex);
  }, [filteredApplications, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-4">Application Management</h3>
      
      {actionSuccess && (
        <Alert variant="success" className="mb-4">
          {actionSuccess}
        </Alert>
      )}
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap gap-3">
            <div style={{ minWidth: '200px' }}>
              <Form.Group>
                <Form.Label>Filter by Trip</Form.Label>
                <Form.Select 
                  value={filterTrip} 
                  onChange={(e) => setFilterTrip(e.target.value)}
                >
                  <option value="">All Trips</option>
                  {trips.map(trip => (
                    <option key={trip._id} value={trip._id}>
                      {trip.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            
            <div style={{ minWidth: '200px' }}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </Form.Group>
            </div>
            
            <div className="d-flex align-items-end">
              <Button 
                variant="outline-secondary"
                onClick={() => {
                  setFilterTrip('');
                  setFilterStatus('');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
      
      {applications.length === 0 ? (
        <Card className="text-center p-4">
          <Card.Body>
            <p className="mb-0">No applications found.</p>
          </Card.Body>
        </Card>
      ) : filteredApplications.length === 0 ? (
        <Card className="text-center p-4">
          <Card.Body>
            <p className="mb-0">No applications match the selected filters.</p>
          </Card.Body>
        </Card>
      ) : (
        <Table responsive striped hover>
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Trip</th>
              <th>Applied On</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedApplications.map(application => (
              <tr key={application._id}>
                <td>
                  <div>
                    <strong>{application.userId.name}</strong>
                  </div>
                  <div className="text-muted small">
                    {application.userId.email}
                  </div>
                </td>
                <td>
                  <div>{application.tripId.name}</div>
                  <div className="text-muted small">
                    {format(new Date(application.tripId.startDate), 'MMM d, yyyy')}
                  </div>
                </td>
                <td>
                  {format(new Date(application.createdAt), 'MMM d, yyyy')}
                </td>
                <td>
                  <Badge bg={
                    application.status === 'pending' ? 'warning' :
                    application.status === 'approved' ? 'success' : 'danger'
                  }>
                    {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleOpenDetailsModal(application)}
                    >
                      View Details
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {filteredApplications.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredApplications.length}
          itemsPerPage={itemsPerPage}
        />
      )}
      
      {/* Application Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Application Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <div>
              <h5 className="mb-3">Applicant Information</h5>
              <Table bordered>
                <tbody>
                  <tr>
                    <th style={{ width: '180px' }}>Name</th>
                    <td>{selectedApplication.userId.name}</td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{selectedApplication.userId.email}</td>
                  </tr>
                  <tr>
                    <th>Certification Level</th>
                    <td>{selectedApplication.userId.certificationLevel}</td>
                  </tr>
                  <tr>
                    <th>Number of Dives</th>
                    <td>{selectedApplication.userId.numberOfDives}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <Badge bg={
                        selectedApplication.status === 'pending' ? 'warning' :
                        selectedApplication.status === 'approved' ? 'success' : 'danger'
                      }>
                        {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </Table>
              
              <h5 className="mt-4 mb-3">Trip Information</h5>
              <Table bordered>
                <tbody>
                  <tr>
                    <th style={{ width: '180px' }}>Trip Name</th>
                    <td>{selectedApplication.tripId.name}</td>
                  </tr>
                  <tr>
                    <th>Location</th>
                    <td>{selectedApplication.tripId.location}</td>
                  </tr>
                  <tr>
                    <th>Dates</th>
                    <td>
                      {format(new Date(selectedApplication.tripId.startDate), 'MMM d, yyyy')} - 
                      {format(new Date(selectedApplication.tripId.endDate), 'MMM d, yyyy')}
                    </td>
                  </tr>
                  <tr>
                    <th>Capacity</th>
                    <td>
                      {selectedApplication.tripId.approvedCount || 0}/{selectedApplication.tripId.capacity}
                    </td>
                  </tr>
                </tbody>
              </Table>
              
              <h5 className="mt-4 mb-3">Application Details</h5>
              <Table bordered>
                <tbody>
                  <tr>
                    <th style={{ width: '180px' }}>Applied On</th>
                    <td>{format(new Date(selectedApplication.createdAt), 'MMM d, yyyy h:mm a')}</td>
                  </tr>
                  <tr>
                    <th>Message</th>
                    <td>{selectedApplication.message}</td>
                  </tr>
                  <tr>
                    <th>Experience</th>
                    <td>{selectedApplication.experience}</td>
                  </tr>
                  <tr>
                    <th>Emergency Contact</th>
                    <td>{selectedApplication.emergencyContact}</td>
                  </tr>
                  {selectedApplication.specialRequirements && (
                    <tr>
                      <th>Special Requirements</th>
                      <td>{selectedApplication.specialRequirements}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              
              <h5 className="mt-4 mb-3">Admin Response</h5>
              <Form.Group className="mb-3">
                <Form.Label>Comments (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminComments}
                  onChange={(e) => setAdminComments(e.target.value)}
                  placeholder="Add comments about this application (will be visible to the applicant)"
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          
          {selectedApplication && selectedApplication.status === 'pending' && (
            <>
              <Button 
                variant="danger" 
                onClick={() => handleUpdateStatus(selectedApplication._id, 'rejected')}
              >
                Reject
              </Button>
              <Button 
                variant="success" 
                onClick={() => handleUpdateStatus(selectedApplication._id, 'approved')}
              >
                Approve
              </Button>
            </>
          )}
          
          {selectedApplication && selectedApplication.status === 'approved' && (
            <Button 
              variant="danger" 
              onClick={() => handleUpdateStatus(selectedApplication._id, 'rejected')}
            >
              Change to Rejected
            </Button>
          )}
          
          {selectedApplication && selectedApplication.status === 'rejected' && (
            <Button 
              variant="success" 
              onClick={() => handleUpdateStatus(selectedApplication._id, 'approved')}
            >
              Change to Approved
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApplicationManagement;